/**
 * The on-disk shape of a page, and the translation to and from the shape the
 * editing system works in.
 *
 * The CMS thinks in "one bag of fields per language". Left alone it writes that
 * straight out, which means the structure of a page - which section is a text
 * block, how wide it is, what colour it sits on - is repeated in full for every
 * language, interleaved with the words. That file is unpleasant to read, and
 * worse, the structure can drift apart between the languages.
 *
 * So the file is written like this instead:
 *
 *     {
 *       "de": {
 *         "published": true,
 *         "title": "…",
 *         "layout":   [ { "id": "einstieg", "type": "text", "tone": "offWhite" } ],
 *         "sections": { "einstieg": { "title": "…", "paragraphs": ["…"] } }
 *       },
 *       "en": {
 *         "title": "…",
 *         "sections": { "einstieg": { "title": "…", "paragraphs": ["…"] } }
 *       }
 *     }
 *
 * Structure once, words per language. A translator's diff now touches only
 * words, and the two languages cannot disagree about structure because there
 * is only one copy of it.
 *
 * The top level is the two languages and nothing else, matching the other
 * files in src/content/. The layout sits inside the leading language rather
 * than beside it, which is not an accident: German *is* the structure of
 * record here, and the file now says so instead of leaving it to a comment.
 *
 * Section keys come from a section's anchor when it has one, so a file reads
 * `"sections": { "kontakt": … }` rather than `"s3"`. Sections without an
 * anchor fall back to their position.
 *
 * ── Where this runs ──────────────────────────────────────────────────────────
 * Both sides of the wall, from this one file:
 *  - in the browser, registered with the CMS as a custom format
 *    (scripts/generate-admin-format.js compiles it to public/admin/);
 *  - in the app and the prerenderer, via cmsPageRepository.ts.
 *
 * That is why it has no imports and no dependencies. Two copies of this
 * transformation that disagreed would corrupt content on save, so there is one.
 */

/** Fields that describe a section rather than say anything. */
const STRUCTURE_KEYS = ['type', 'tone', 'width', 'align', 'anchor', 'compact'];

/**
 * Page-level fields that are the same in every language, so they are written
 * once, with the leading language, and handed back to every locale on the way
 * into the editing system. `icon` is one of them: a symbol is not translated.
 * Neither is `titleImage`, the artwork a page may carry in place of its written
 * heading - a second copy of it per language would only ever drift.
 */
const SETTINGS_KEYS = ['published', 'showInNav', 'icon', 'titleImage'];

/** Flat in the CMS, grouped in the file. */
const SEO_KEYS: Array<[string, string]> = [
  ['seoTitle', 'title'],
  ['seoDescription', 'description'],
  ['seoKeywords', 'keywords'],
];

/**
 * Kept in step with DEFAULT_LANGUAGE in shared/constants/routes.ts. Not
 * imported, because this module has to survive being compiled on its own for
 * the browser; callers inside the app pass the real value.
 */
export const DEFAULT_LOCALE = 'de';

type Bag = Record<string, unknown>;

const isRecord = (value: unknown): value is Bag =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isBlank = (value: unknown): boolean =>
  value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0);

/** Drops keys with nothing in them, so the file has no rows of `""`. */
const compact = (bag: Bag): Bag => {
  const out: Bag = {};
  Object.entries(bag).forEach(([key, value]) => {
    if (!isBlank(value)) {
      out[key] = value;
    }
  });
  return out;
};

/** Positional fallback id, used when a section has no anchor to name it by. */
const positionalId = (index: number): string => `s${index + 1}`;

/** Anchor text reduced to something usable as a key. */
const slugify = (value: unknown): string =>
  typeof value === 'string'
    ? value
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
    : '';

/**
 * Names for the sections, in layout order. An anchor makes a section
 * self-describing in the file; anything else, or a repeat, falls back to the
 * position so that keys stay unique.
 */
const sectionIds = (blocks: unknown[]): string[] => {
  const taken = new Set<string>();

  return blocks.map((block, index) => {
    const fromAnchor = isRecord(block) ? slugify(block.anchor) : '';
    const id = fromAnchor && !taken.has(fromAnchor) ? fromAnchor : positionalId(index);
    taken.add(id);
    return id;
  });
};

/**
 * Where a file keeps its layout, or null if it has none.
 *
 * Three shapes have to be readable: the current one (layout inside the leading
 * language), the first attempt at it (layout beside the languages), and a file
 * written in the CMS's own shape. Only the first is produced; the others are
 * recognised so that nothing that once worked stops working.
 */
const layoutHolder = (
  data: Bag,
  defaultLocale: string
): { layout: unknown[]; settings: Bag } | null => {
  if (Array.isArray(data.layout)) {
    return { layout: data.layout, settings: isRecord(data.settings) ? data.settings : {} };
  }

  const locales = Object.keys(data).filter((key) => isRecord(data[key]));
  const leadingLocale = isRecord(data[defaultLocale]) ? defaultLocale : locales[0];
  const leading = leadingLocale ? (data[leadingLocale] as Bag) : undefined;

  if (leading && Array.isArray(leading.layout)) {
    return {
      layout: leading.layout,
      settings: compact(
        Object.fromEntries(SETTINGS_KEYS.map((key) => [key, leading[key]])) as Bag
      ),
    };
  }

  return null;
};

/** True for a file written in this project's shape rather than the CMS's. */
export const isFileShape = (data: unknown, defaultLocale: string = DEFAULT_LOCALE): boolean =>
  isRecord(data) && layoutHolder(data, defaultLocale) !== null;

/** True for a file already in the *current* shape, layout inside the language. */
export const isCurrentFileShape = (data: unknown, defaultLocale: string = DEFAULT_LOCALE): boolean =>
  isRecord(data) &&
  !Array.isArray(data.layout) &&
  isRecord(data[defaultLocale]) &&
  Array.isArray((data[defaultLocale] as Bag).layout);

/* -------------------------------------------------------------------------- */
/* CMS  ->  file                                                              */
/* -------------------------------------------------------------------------- */

const splitBlock = (raw: unknown): { structure: Bag; text: Bag } => {
  const structure: Bag = {};
  const text: Bag = {};

  if (isRecord(raw)) {
    Object.entries(raw).forEach(([key, value]) => {
      if (STRUCTURE_KEYS.includes(key)) {
        structure[key] = value;
      } else {
        text[key] = value;
      }
    });
  }

  return { structure: compact(structure), text: compact(text) };
};

export const toFileShape = (cmsData: unknown, defaultLocale: string = DEFAULT_LOCALE): Bag => {
  if (!isRecord(cmsData)) {
    return {};
  }

  const locales = Object.keys(cmsData).filter((key) => isRecord(cmsData[key]));
  const leadingLocale = cmsData[defaultLocale] ? defaultLocale : locales[0];
  const leading = (cmsData[leadingLocale] ?? {}) as Bag;
  const leadingBlocks = Array.isArray(leading.blocks) ? leading.blocks : [];
  const ids = sectionIds(leadingBlocks);

  // The leading language owns the structure; every other language is words.
  const layout = leadingBlocks.map((block, index) => ({
    id: ids[index],
    ...splitBlock(block).structure,
  }));

  const file: Bag = {};

  locales.forEach((locale) => {
    const slice = cmsData[locale] as Bag;
    const blocks = Array.isArray(slice.blocks) ? slice.blocks : [];

    const sections: Bag = {};
    // Keyed by the leading language's ids: structure and words stay aligned by
    // position, which is also how the CMS presents the two languages.
    ids.forEach((id, index) => {
      const { text } = splitBlock(blocks[index]);
      if (Object.keys(text).length > 0) {
        sections[id] = text;
      }
    });

    const seo = compact(
      Object.fromEntries(SEO_KEYS.map(([cmsKey, fileKey]) => [fileKey, slice[cmsKey]])) as Bag
    );

    const pageText = compact({
      title: slice.title,
      eyebrow: slice.eyebrow,
      lead: slice.lead,
      navLabel: slice.navLabel,
    });

    const isLeading = locale === leadingLocale;

    file[locale] = {
      // Settings and structure live with the leading language only.
      ...(isLeading
        ? compact(Object.fromEntries(SETTINGS_KEYS.map((key) => [key, leading[key]])) as Bag)
        : {}),
      ...pageText,
      ...(Object.keys(seo).length > 0 ? { seo } : {}),
      ...(isLeading ? { layout } : {}),
      sections,
    };
  });

  return file;
};

/* -------------------------------------------------------------------------- */
/* file  ->  CMS                                                              */
/* -------------------------------------------------------------------------- */

export const toCmsShape = (fileData: unknown, defaultLocale: string = DEFAULT_LOCALE): Bag => {
  if (!isRecord(fileData)) {
    return {};
  }

  const holder = layoutHolder(fileData, defaultLocale);

  // A file written by hand in the CMS's own shape is already what the CMS
  // expects. Passing it through unchanged means such a file still opens and
  // still renders.
  if (!holder) {
    return fileData;
  }

  const { settings } = holder;
  const layout = holder.layout.filter(isRecord);
  const locales = Object.keys(fileData).filter(
    (key) => key !== 'settings' && key !== 'layout' && isRecord(fileData[key])
  );

  const cms: Bag = {};

  locales.forEach((locale) => {
    const slice = fileData[locale] as Bag;
    const sections = isRecord(slice.sections) ? slice.sections : {};
    const seo = isRecord(slice.seo) ? slice.seo : {};

    const blocks = layout.map((entry, index) => {
      const { id, ...structure } = entry;
      // By name, falling back to position - so that renaming an anchor by hand
      // without renaming the section key does not silently drop its words.
      const byId = sections[String(id)];
      const byPosition = sections[positionalId(index)];
      const text = isRecord(byId) ? byId : isRecord(byPosition) ? byPosition : {};
      // Structure first: every language gets the full section, including the
      // type, which is exactly what the CMS fails to write on its own.
      return { ...structure, ...text };
    });

    cms[locale] = {
      ...settings,
      // `layout` belongs to the file, not to the entry the CMS edits.
      ...compact({
        title: slice.title,
        eyebrow: slice.eyebrow,
        lead: slice.lead,
        navLabel: slice.navLabel,
      }),
      ...Object.fromEntries(SEO_KEYS.map(([cmsKey, fileKey]) => [cmsKey, seo[fileKey] ?? ''])),
      blocks,
    };
  });

  return cms;
};
